import { ConfirmAssociationDetail } from "@/pages/procedures/components/confirm-association/confirm-association-detail";
import FormCorrection from "@/pages/procedures/components/form/form-correction";
import { FormDetail } from "@/pages/procedures/components/form/form-detail";
import { FormInterns } from "@/pages/procedures/components/form/form-interns";
import { ProcedureFormsList } from "@/pages/procedures/components/form/form-list";
import { PaymentDetail } from "@/pages/procedures/components/payment/PaymentDetail";
import ResolutionPage from "@/pages/procedures/components/resolution/ResolutionDetail";
import SignatureDetail from "@/pages/procedures/components/signature/signature-detail";
import RoleGuard from "@/pages/role-guard";
import {
  createFormalityRequestBreadcrumb,
  createGetFormDetailBreadcrumb,
} from "@/queries/formality-request";
import { Permissions } from "@/utils/permissions";
import type { QueryClient } from "@tanstack/react-query";
import { lazy } from "react";
import { makeHandle, withNotFoundRedirect } from "./router.helpers";

const ProcedureDetailPage = lazy(() =>
  import("@/pages/procedures").then((m) => ({
    default: m.ProcedureDetailPage,
  })),
);

export const createTramiteRoute = (queryClient: QueryClient) => ({
  path: "tramite/:procedureId",
  element: (
    <RoleGuard permissions={[Permissions.formality_requests.read]}>
      <ProcedureDetailPage />
    </RoleGuard>
  ),
  handle: makeHandle({ label: "Trámites" }),
  children: [
    {
      index: true,
      element: <ProcedureFormsList />,
      loader: ({ params }: { params: Record<string, string | undefined> }) =>
        withNotFoundRedirect(
          () =>
            createFormalityRequestBreadcrumb(
              queryClient,
              params.procedureId || "",
            )(),
          "/",
        ),
      handle: {
        breadcrumb: (match: { loaderData?: { data?: { name?: string } } }) => [
          { label: "Mis Trámites", path: "/" },
          { label: match?.loaderData?.data?.name || "Detalle" },
        ],
      },
    },
    {
      path: "correccion/:formId",
      element: (
        <RoleGuard permissions={[Permissions.widgets.submissions]}>
          <FormCorrection />
        </RoleGuard>
      ),
      loader: ({ params }: { params: Record<string, string | undefined> }) =>
        withNotFoundRedirect(
          () =>
            createGetFormDetailBreadcrumb(
              queryClient,
              params.formId || "",
              params.procedureId || "",
            )(),
          `/tramite/${params.procedureId}`,
        ),
      handle: {
        breadcrumb: (match: {
          loaderData?: { data?: { name?: string } };
          params: { procedureId?: string };
        }) => [
          { label: "Mis Trámites", path: "/" },
          { label: "Detalle", path: `/tramite/${match.params.procedureId}` },
          { label: match?.loaderData?.data?.name || "Nombre del Formulario" },
        ],
      },
    },
    {
      path: "firma/:singRequestId",
      element: (
        <RoleGuard permissions={[Permissions.widgets.submissions]}>
          <SignatureDetail />
        </RoleGuard>
      ),
      handle: {
        breadcrumb: (match: { params?: { procedureId?: string } }) => [
          { label: "Mis Trámites", path: "/" },
          { label: "Detalle", path: `/tramite/${match.params?.procedureId}` },
          { label: "Firma de documentos" },
        ],
      },
    },
    {
      path: "pago/:paymentRequestId",
      element: (
        <RoleGuard permissions={[Permissions.widgets.submissions]}>
          <PaymentDetail />
        </RoleGuard>
      ),
      handle: {
        breadcrumb: (match: { params?: { procedureId?: string } }) => [
          { label: "Mis Trámites", path: "/" },
          { label: "Detalle", path: `/tramite/${match.params?.procedureId}` },
          { label: "Detalle de pago" },
        ],
      },
    },
    {
      path: "resolucion",
      element: <ResolutionPage />,
      handle: {
        breadcrumb: (match: { params?: { procedureId?: string } }) => [
          { label: "Mis Trámites", path: "/" },
          { label: "Detalle", path: `/tramite/${match.params?.procedureId}` },
          { label: "Resolución" },
        ],
      },
    },
    {
      path: ":formId",
      element: <FormDetail />,
      loader: ({ params }: { params: Record<string, string | undefined> }) =>
        withNotFoundRedirect(
          () =>
            createGetFormDetailBreadcrumb(
              queryClient,
              params.formId || "",
              params.procedureId || "",
            )(),
          `/tramite/${params.procedureId}`,
        ),
      handle: {
        breadcrumb: (match: {
          params?: { procedureId?: string };
          loaderData?: { data?: { name?: string } };
        }) => [
          { label: "Mis Trámites", path: "/" },
          { label: "Detalle", path: `/tramite/${match.params?.procedureId}` },
          { label: match?.loaderData?.data?.name || "Nombre del Formulario" },
        ],
      },
    },
    {
      path: "formulario-interno/:formId",
      element: <FormInterns />,
      loader: ({ params }: { params: Record<string, string | undefined> }) =>
        withNotFoundRedirect(
          () =>
            createGetFormDetailBreadcrumb(
              queryClient,
              params.formId || "",
              params.procedureId || "",
            )(),
          `/tramite/${params.procedureId}`,
        ),
      handle: {
        breadcrumb: (match: {
          params?: { procedureId?: string };
          loaderData?: { data?: { name?: string } };
        }) => [
          { label: "Mis Trámites", path: "/" },
          { label: "Detalle", path: `/tramite/${match.params?.procedureId}` },
          { label: match?.loaderData?.data?.name || "Nombre del Formulario" },
        ],
      },
    },
    {
      path: "confirmar-asociacion/:nodeExecutionId",
      element: <ConfirmAssociationDetail />,
      handle: makeHandle(
        { label: "Mis Trámites", path: "/" },
        { label: "Confirmar asociación" },
      ),
    },
  ],
});
